import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import useOutsideClick from 'utils/useOutsideClick';
import Search from './Search';
import { Props, SELECT_MODES } from './Select.types';

import './_Select.scss';
import { Option } from '../../interface/globalInterface';

const SEARCHABLE_ITEMS_AMOUNT_BOUNDARY = 10;

const Select: React.FC<Props> = ({
  variant,
  options,
  name,
  onSelected,
  disabled,
  selected,
  searchable = options.length > SEARCHABLE_ITEMS_AMOUNT_BOUNDARY,
  isRequired = false,
  inline = false,
  tight = false,
  placeholderText = 'Please select..',
  inputText,
  noOptionsText = 'No options',
  showError,
  handleBlur,
  tooltip,
}: Props) => {
  const [clicked, setClicked] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const [activeMode, setActiveMode] = useState<SELECT_MODES>(SELECT_MODES.FIXED);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);

  const selectOptionHandler = (option: any) => () => {
    onSelected(option);
    setClicked(false);
  };

  useEffect(() => {
    if (searchable) {
      setActiveMode(clicked ? SELECT_MODES.SEARCH : SELECT_MODES.FIXED);
    }
  }, [clicked, searchable]);

  const getOptions = useCallback(() => {
    return activeMode === SELECT_MODES.SEARCH ? filteredOptions : options;
  }, [activeMode, filteredOptions, options]);

  useOutsideClick(ref, () => {
    setClicked(false);
    setFilteredOptions(options);
  });

  const wrapperClass = classNames('select-box', {
    'select-box--inline': inline,
    'select-box--tight': tight,
  });

  const handleClickDropdownContainer = () => {
    if (activeMode === SELECT_MODES.FIXED) {
      setClicked(!clicked);
      setFilteredOptions(options);
    }
  };

  const baseClassName = variant === 'new' ? 'select-box-new' : 'select-box';

  return (
    <div
      className={variant === 'new' ? `select-box-new${disabled ? '--disabled' : ''}` : wrapperClass}
      tabIndex={0}
      onBlur={handleBlur}>
      {disabled ? (
        <label className={`${baseClassName}__option`} aria-hidden={false}>
          {selected.label || 'Option not selected yet'}
        </label>
      ) : (
        <>
          <div
            className={`${baseClassName}__current${clicked ? ` ${baseClassName}__current--opened` : ''}`}
            tabIndex={1}
            onClick={handleClickDropdownContainer}
            ref={ref}>
            {activeMode === SELECT_MODES.FIXED ? (
              <div className={`${baseClassName}__value`}>
                <>
                  <input
                    className={`${baseClassName}__input`}
                    type="radio"
                    id={'please-select'}
                    disabled={true}
                    name={`custom-select__${name}`}
                    checked={!selected.value}
                    onChange={() => {}}
                  />
                  {!selected.value && (
                    <p className={`${baseClassName}__input-text ${showError ? 'error' : ''}`}>
                      {inputText || getOptions().length > 0 ? placeholderText : noOptionsText}
                    </p>
                  )}
                </>
              </div>
            ) : (
              <div className={`${baseClassName}__search`}>
                <Search items={options} onSearch={setFilteredOptions} />
              </div>
            )}

            {getOptions()
              .filter((option) => option.value === selected.value && activeMode === SELECT_MODES.FIXED)
              .map((option, index) => (
                <div className={`${baseClassName}__value`} key={`select-value-${option.value}`}>
                  <input
                    className={`${baseClassName}__input`}
                    type="radio"
                    id={index.toString()}
                    value={option.value}
                    name={`custom-select__${name}`}
                    checked={option.value === selected.value}
                    onChange={() => {}}
                  />
                  <p className={`${baseClassName}__input-text ${showError ? 'error' : ''}`}>{option.option}</p>
                </div>
              ))}
            <svg
              className={`${baseClassName}__icon ${variant === 'newUi' && 'new-ui'}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 88 54"
              onClick={() => setClicked(false)}>
              <path d="M78.148 2.217L43.882 36.483 9.617 2.217l-7.984 7.984 34.265 34.266 7.986 7.985 7.984-7.984 34.265-34.265z"></path>
            </svg>
          </div>
          {clicked && (
            <ul className={`${baseClassName}__list`}>
              {(!selected || !isRequired) && (
                <li
                  onClick={selectOptionHandler({
                    value: '',
                  })}>
                  <label className={`${baseClassName}__option`} htmlFor={'please-select'} aria-hidden={true}>
                    {placeholderText}
                  </label>
                </li>
              )}
              {getOptions().length > 0 ? (
                getOptions().map((option, index) => (
                  <li key={`option-label-${option.value}`} onClick={selectOptionHandler(option)}>
                    <label className={`${baseClassName}__option`} htmlFor={index.toString()} aria-hidden={true}>
                      {option.option}
                    </label>
                  </li>
                ))
              ) : (
                <li>
                  <label className={`${baseClassName}__option`} aria-hidden={true}>
                    {noOptionsText}
                  </label>
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default Select;
